import React, { useEffect, useState } from 'react';
import ConsensusView from '../components/consensus/ConsensusView';
import { getWarRoomConsensus } from '../services/warRoomConsensusApi';
import type { WarRoomConsensus } from '../types/warRoom';
import { useParams, useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import AdjusterQABot from '../components/AdjusterQABot';
import { FinancialValue } from '../components/shared/FinancialValue';
import { StateChip, ChipState } from '../components/shared/StateChip';
import { formatCurrency } from '../utils/financialFormat';
import { 
  adjusterAnalyzerApi,
  AdjusterAnalysisResult,
  DocumentationGap,
  CausationAssessment,
  ReductionArgument
} from '../services/adjusterAnalyzerApi';
import {
  ArrowLeft,
  AlertTriangle,
  CheckCircle,
  XCircle,
  TrendingDown,
  TrendingUp,
  FileText,
  RefreshCw,
  DollarSign,
  ShieldAlert,
  Scale,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  Info,
  Clock,
  BarChart3,
  PieChart,
  MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';

function humanizeEnum(str: string): string {
  if (!str) return '';
  return str
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function severityToState(kind: 'assessment' | 'risk' | 'gap' | 'reduction' | 'causation', value: string): ChipState {
  const val = value?.toUpperCase?.() || '';
  
  switch (kind) {
    case 'assessment':
      if (val === 'SUPPORTED') return 'verified';
      if (val === 'PARTIALLY_SUPPORTED') return 'warning';
      if (val === 'QUESTIONED') return 'needs-human';
      if (val === 'DENIED') return 'blocker';
      return 'dormant';
      
    case 'risk':
      if (val === 'HIGH') return 'needs-human';
      if (val === 'MEDIUM') return 'warning';
      if (val === 'LOW') return 'verified';
      return 'dormant';
      
    case 'gap':
      if (val === 'CRITICAL') return 'needs-human';
      if (val === 'MAJOR') return 'warning';
      if (val === 'MINOR' || val === 'CLARIFICATION') return 'dormant';
      return 'dormant';
      
    case 'reduction':
      if (val === 'STRONG') return 'needs-human';
      if (val === 'MODERATE') return 'warning';
      if (val === 'WEAK') return 'dormant';
      return 'dormant';
      
    case 'causation':
      if (val === 'CLEAR' || val === 'PROBABLE') return 'verified';
      if (val === 'UNCERTAIN') return 'warning';
      if (val === 'UNLIKELY') return 'needs-human';
      if (val === 'UNRELATED') return 'blocker';
      return 'dormant';
      
    default:
      return 'dormant';
  }
}

export default function AdjusterAnalyzerView() {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState<AdjusterAnalysisResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [consensus, setConsensus] = useState<WarRoomConsensus | null>(null);
  const [consensusLoading, setConsensusLoading] = useState(true);
  const [consensusError, setConsensusError] = useState<unknown>(null);

  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['summary', 'claim_items'])
  );
  
  const [qaBotOpen, setQaBotOpen] = useState(false);

  useEffect(() => {
    if (reportId) {
      fetchAnalysis();
    }
  }, [reportId]);

  useEffect(() => {
    if (!reportId) return;
    let cancelled = false;
    setConsensusLoading(true);
    getWarRoomConsensus(reportId)
      .then((data) => { if (!cancelled) setConsensus(data); })
      .catch((err) => { if (!cancelled) setConsensusError(err); })
      .finally(() => { if (!cancelled) setConsensusLoading(false); });
    return () => { cancelled = true; };
  }, [reportId]);

  const fetchAnalysis = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adjusterAnalyzerApi.getAnalysis(reportId!);
      setAnalysis(data);
    } catch (err: any) {
      console.error('Failed to fetch analysis:', err);
      if (err.message.includes('not found')) {
        setError('No analysis found for this report. Click "Run Analysis" to generate one.');
      } else {
        setError(err.message || 'Failed to load analysis');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRunAnalysis = async (forceRefresh: boolean = false) => {
    if (!reportId) return;
    
    try {
      setRunning(true);
      const result = await adjusterAnalyzerApi.runAnalysis(reportId, forceRefresh);
      setAnalysis(result.result);
      setError(null);
      toast.success('Analysis completed successfully');
    } catch (err: any) {
      toast.error(err.message || 'Failed to run analysis');
    } finally {
      setRunning(false);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  const formatPercent = (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="w-8 h-8 animate-spin" style={{ color: 'var(--color-brand)' }} />
          <span className="cs-body ml-2" style={{ color: 'var(--color-text-secondary)' }}>Loading analysis...</span>
        </div>
      </AppLayout>
    );
  }

  const analysisComplete = !!(
    analysis &&
    analysis.executive_summary &&
    analysis.claim_items &&
    analysis.documentation_analysis &&
    analysis.causation_analysis &&
    analysis.reduction_arguments &&
    analysis.risk_assessment &&
    analysis.conservative_pnl &&
    analysis.metadata
  );

  return (
    <AppLayout>
      <div className="p-6 max-w-6xl mx-auto pb-24" data-testid="adjuster-analyzer-view">
        <div className="flex items-center justify-between mb-6">
          <div>
            <button
              onClick={() => navigate(`/report/${reportId}`)}
              className="cs-body flex items-center gap-2 mb-2 transition-colors"
              style={{ color: 'var(--color-text-secondary)' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-text-primary)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-secondary)'; }}
              data-testid="back-to-report-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Report
            </button>
            <h1 className="cs-display" style={{ color: 'var(--color-text-primary)' }}>Adjuster Analysis</h1>
            <p className="cs-meta mt-1">Conservative insurance adjuster perspective on this claim</p>
          </div>

          <div className="flex gap-3">
            {analysis && (
              <button
                onClick={() => handleRunAnalysis(true)}
                disabled={running}
                className="cs-label flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
                style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-bg-surface)'; }}
                data-testid="refresh-analysis-btn"
              >
                <RefreshCw className={`w-4 h-4 ${running ? 'animate-spin' : ''}`} />
                Re-run Analysis
              </button>
            )}
            {!analysis && (
              <button
                onClick={() => handleRunAnalysis(false)}
                disabled={running}
                className="cs-label flex items-center gap-2 px-4 py-2 rounded-lg transition-colors hover:opacity-90"
                style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                data-testid="run-analysis-btn"
              >
                {running ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <BarChart3 className="w-4 h-4" />
                )}
                Run Analysis
              </button>
            )}
          </div>
        </div>

        {error && !analysis && (
          <div className="rounded-lg p-6 text-center" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
            <AlertCircle className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--color-brand)' }} />
            <h2 className="cs-h3 mb-2" style={{ color: 'var(--color-text-primary)' }}>Analysis Not Available</h2>
            <p className="cs-body mb-4" style={{ color: 'var(--color-text-secondary)' }}>{error}</p>
            <button
              onClick={() => handleRunAnalysis(false)}
              disabled={running}
              className="cs-label px-6 py-2 rounded-lg transition-colors hover:opacity-90"
              style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
            >
              {running ? 'Running...' : 'Run Analysis Now'}
            </button>
          </div>
        )}

        {analysis && !analysisComplete && (
          <div
            className="rounded-lg p-6 text-center"
            style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
            data-testid="adjuster-analysis-unavailable"
          >
            <AlertCircle className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--color-text-muted)' }} />
            <h2 className="cs-h3 mb-2" style={{ color: 'var(--color-text-primary)' }}>Adjuster analysis unavailable</h2>
            <p className="cs-body" style={{ color: 'var(--color-text-muted)' }}>This analysis is incomplete — re-run it to regenerate the full result.</p>
          </div>
        )}

        {analysisComplete && (
          <div className="space-y-6">
            <VerdictBand analysis={analysis} />
            
            <CollapsibleSection
              title="Executive Summary"
              icon={PieChart}
              expanded={expandedSections.has('summary')}
              onToggle={() => toggleSection('summary')}
            >
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <SummaryCard
                  label="Supported"
                  value={formatCurrency(analysis.executive_summary.supported_total)}
                  chipState="verified"
                  icon={CheckCircle}
                />
                <SummaryCard
                  label="Questioned"
                  value={formatCurrency(analysis.executive_summary.questioned_total)}
                  chipState="warning"
                  icon={AlertTriangle}
                />
                <SummaryCard
                  label="Denied"
                  value={formatCurrency(analysis.executive_summary.denied_total)}
                  chipState="blocker"
                  icon={XCircle}
                />
                <SummaryCard
                  label="Attorney Claims"
                  value={formatCurrency(analysis.executive_summary.attorney_claimed_total)}
                  icon={Scale}
                />
              </div>
              
              <div className="rounded-lg p-4 mb-4" style={{ background: 'var(--color-bg-elevated)' }}>
                <div className="flex items-center justify-between">
                  <span className="cs-body" style={{ color: 'var(--color-text-secondary)' }}>Variance from Attorney Position</span>
                  <div className="flex items-center gap-4">
                    {(() => {
                      const claimed = analysis.executive_summary.attorney_claimed_total;
                      const supported = analysis.executive_summary.supported_total;
                      const gap = Math.max(0, claimed - supported);
                      const pct = claimed > 0 ? (gap / claimed) * 100 : 0;
                      return (
                        <>
                          <span className="font-mono tabular-nums cs-figure-lg" style={{ color: 'var(--color-brand)' }}>
                            (<FinancialValue value={gap} />)
                          </span>
                          <span className="font-mono tabular-nums cs-figure" style={{ color: 'var(--color-text-secondary)' }}>
                            ({pct.toFixed(0)}%)
                          </span>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {analysis.executive_summary.key_reduction_factors?.length > 0 && (
                <div>
                  <h4 className="cs-label mb-2" style={{ color: 'var(--color-text-secondary)' }}>Key Reduction Factors</h4>
                  <ul className="space-y-1">
                    {analysis.executive_summary.key_reduction_factors.map((factor, idx) => (
                      <li key={idx} className="cs-body flex items-start gap-2" style={{ color: 'var(--color-text-secondary)' }}>
                        <TrendingDown className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--color-text-muted)' }} />
                        {factor}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CollapsibleSection>

            <CollapsibleSection
              title="Risk Assessment"
              icon={ShieldAlert}
              expanded={expandedSections.has('risk')}
              onToggle={() => toggleSection('risk')}
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <RiskCard
                  label="Underpayment Risk"
                  level={analysis.risk_assessment.underpayment_risk}
                  description="Risk of paying less than actually owed"
                />
                <RiskCard
                  label="Litigation Risk"
                  level={analysis.risk_assessment.litigation_risk}
                  description="Risk of legal action from insured"
                />
                <RiskCard
                  label="Bad Faith Risk"
                  level={analysis.risk_assessment.bad_faith_risk}
                  description="Risk of bad faith claim"
                />
              </div>
              {analysis.risk_assessment.risk_notes && (
                <div className="rounded-lg p-4" style={{ background: 'var(--color-bg-elevated)' }}>
                  <p className="cs-body" style={{ color: 'var(--color-text-secondary)' }}>{analysis.risk_assessment.risk_notes}</p>
                </div>
              )}
            </CollapsibleSection>

            {analysis.carrier_bridge && (
              <CollapsibleSection
                title="Carrier Position — Reconciliation"
                icon={FileText}
                expanded={expandedSections.has('claim_items')}
                onToggle={() => toggleSection('claim_items')}
              >
                <CarrierBridgeLedger bridge={analysis.carrier_bridge} />
              </CollapsibleSection>
            )}

            <CollapsibleSection
              title={`Documentation Gaps (${analysis.documentation_analysis.gaps?.length || 0})`}
              icon={AlertTriangle}
              expanded={expandedSections.has('gaps')}
              onToggle={() => toggleSection('gaps')}
              badge={analysis.documentation_analysis.gaps?.filter(g => g.category === 'CRITICAL').length || 0}
            >
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="cs-body" style={{ color: 'var(--color-text-secondary)' }}>Overall Completeness:</span>
                  <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'var(--color-bg-elevated)' }}>
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${analysis.documentation_analysis.overall_completeness ?? 0}%`, background: 'var(--color-brand)' }}
                    />
                  </div>
                  <span className="font-mono tabular-nums cs-label" style={{ color: 'var(--color-text-primary)' }}>
                    {(analysis.documentation_analysis.overall_completeness ?? 0).toFixed(0)}%
                  </span>
                </div>
              </div>

              {(!analysis.documentation_analysis.gaps || analysis.documentation_analysis.gaps.length === 0) ? (
                <div className="cs-body text-center py-4 flex items-center justify-center gap-2" style={{ color: 'var(--color-text-secondary)' }}>
                  <CheckCircle className="w-5 h-5" />
                  No documentation gaps identified
                </div>
              ) : (
                <div className="space-y-3">
                  {analysis.documentation_analysis.gaps.map((gap) => (
                    <GapCard key={gap.id} gap={gap} />
                  ))}
                </div>
              )}
            </CollapsibleSection>

            <CollapsibleSection
              title="Causation Analysis"
              icon={TrendingUp}
              expanded={expandedSections.has('causation')}
              onToggle={() => toggleSection('causation')}
            >
              <div className="mb-4 flex items-center gap-2">
                <span className="cs-body" style={{ color: 'var(--color-text-secondary)' }}>Overall Causation Strength:</span>
                <StateChip
                  state={severityToState('causation', analysis.causation_analysis.overall_causation_strength)}
                  text={humanizeEnum(analysis.causation_analysis.overall_causation_strength)}
                />
              </div>

              {/* Owner (2026-09-07): frame the causation ratings against the NET loss, not the
                  70/15/15 category ghost. These strength ratings ARE the carrier's causation
                  argument (pre-event decline + seasonality); they drive the Causation adjustment
                  in the Carrier Position waterfall above — applied to the BI net loss, not a
                  standalone category payout. */}
              {(() => {
                const causStep = analysis.carrier_bridge?.steps?.find((s) => /causation/i.test(s.label));
                return (
                  <p className="cs-body mb-4 rounded-lg p-3" style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}>
                    These ratings are the carrier's causation argument applied to the BI net loss —
                    they drive the <span style={{ color: 'var(--color-text-primary)' }}>Causation adjustment</span>
                    {causStep ? <> (<FinancialValue value={causStep.amount} cents contra className="inline" />)</> : null} in the
                    Carrier Position reconciliation above, not a standalone category payout. The category
                    rows below are the analytic basis for that single adjustment.
                  </p>
                );
              })()}

              {analysis.causation_analysis.by_category?.length > 0 && (
                <div className="space-y-3">
                  {analysis.causation_analysis.by_category.map((assessment, idx) => (
                    <CausationCard key={idx} assessment={assessment} />
                  ))}
                </div>
              )}
            </CollapsibleSection>

            {/* Conservative P&L block hidden (owner, 2026-09-07): _build_conservative_pnl is a
                self-admitted "simplified" median-baseline toy — median monthly revenue minus
                median monthly ALL-IN expenses (incl. COGS + payroll), 0% growth — so for a
                restaurant it renders a negative baseline/but-for net income that does NOT foot to
                the canonical CP 00 30 engine figure the Carrier Position waterfall anchors on.
                It fed no waterfall number. Retie to the engine but-for is queued
                (KAIROS_QUEUE.md) as a deliberate follow-up; until then it is not shown to carrier
                clients. The field stays in the payload (position.conservative_assumptions + the
                analysisComplete guard read it). */}

            <CollapsibleSection
              title={`Reduction Arguments (${analysis.reduction_arguments.length})`}
              icon={TrendingDown}
              expanded={expandedSections.has('reduction')}
              onToggle={() => toggleSection('reduction')}
            >
              {analysis.reduction_arguments.length === 0 ? (
                <p className="cs-meta text-center py-4">No reduction arguments applied</p>
              ) : (
                <div className="space-y-3">
                  {analysis.reduction_arguments.map((arg) => (
                    <ReductionArgumentCard key={arg.id} argument={arg} />
                  ))}
                </div>
              )}
            </CollapsibleSection>
          </div>
        )}
        
        <ConsensusView
          consensus={consensus}
          isLoading={consensusLoading}
          error={consensusError}
          className="mt-6"
        />
        
        {analysisComplete && (
          <div className="mt-8 pt-6 border-t text-sm" style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }}>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              <span>Analysis ID: {analysis!.id}</span>
              <span>•</span>
              {/* Owner (2026-09-07): don't sit a raw LLM model id on a forensic page. The figures
                  here are computed by the deterministic AdjusterAnalyzer; only the narrative
                  (key reduction factors) is AI-assisted. Say exactly that instead of a model name. */}
              <span>Figures: deterministic analyzer · narrative: AI-assisted</span>
              <span>•</span>
              <span className="font-mono tabular-nums">Processing Time: {analysis!.metadata.processing_time_seconds}s</span>
              <span>•</span>
              <span>Created: {new Date(analysis!.created_at).toLocaleString()}</span>
            </div>
          </div>
        )}
      </div>

      {reportId && (
        <>
          <AdjusterQABot
            reportId={reportId}
            isOpen={qaBotOpen}
            onToggle={() => setQaBotOpen(!qaBotOpen)}
          />
          
          {!qaBotOpen && analysis && (
            <button
              onClick={() => setQaBotOpen(true)}
              className="fixed bottom-6 right-6 flex items-center gap-2 px-4 py-3 rounded-full shadow-lg transition-all z-30 hover:opacity-90"
              style={{ 
                background: 'var(--color-bg-surface)', 
                border: '1px solid var(--color-border)',
                color: 'var(--color-brand)' 
              }}
              title="Ask AI about this analysis"
            >
              <MessageCircle className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
              <span className="cs-label" style={{ color: 'var(--color-brand)' }}>Ask AI</span>
            </button>
          )}
        </>
      )}
    </AppLayout>
  );
}

function VerdictBand({ analysis }: { analysis: AdjusterAnalysisResult }) {
  const es = analysis.executive_summary;
  const supported = es?.supported_total ?? 0;
  const claimed = es?.attorney_claimed_total ?? 0;
  const gap = Math.max(0, claimed - supported);
  const gapPct = claimed > 0 ? (gap / claimed) * 100 : 0;
  const supportedPct = claimed > 0 ? (supported / claimed) * 100 : 0;
  
  const completeness = analysis.documentation_analysis?.overall_completeness ?? 0;
  const causationStrength = analysis.causation_analysis?.overall_causation_strength ?? '';
  const strongArgs = analysis.reduction_arguments?.filter(a => a.strength === 'STRONG').length ?? 0;
  const deniedTotal = es?.denied_total ?? 0;
  
  const hasGaps = (analysis.documentation_analysis?.gaps?.length ?? 0) > 0;
  const causationLabel = humanizeEnum(causationStrength);
  const causationState = severityToState('causation', causationStrength);
  
  return (
    <div 
      className="sticky top-0 z-20 mb-6 p-6 border-b"
      style={{ 
        background: 'var(--color-bg-surface)', 
        borderColor: 'var(--color-border)' 
      }}
    >
      <div className="flex items-end gap-8 mb-4">
        <div className="flex-1">
          <div className="text-xs uppercase tracking-wider mb-1 font-medium" style={{ color: 'var(--color-text-muted)' }}>Carrier Supports</div>
          <div className="text-2xl font-mono tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
            <FinancialValue value={supported} />
          </div>
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-wider mb-1 font-medium" style={{ color: 'var(--color-text-muted)' }}>Attorney Claims</div>
          <div className="text-2xl font-mono tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
            <FinancialValue value={claimed} />
          </div>
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-wider mb-1 font-medium" style={{ color: 'var(--color-text-muted)' }}>Gap</div>
          <div className="text-3xl font-mono tabular-nums" style={{ color: 'var(--color-brand)' }}>
            <FinancialValue value={gap} />
            <span className="text-lg ml-2 font-mono tabular-nums">({gapPct.toFixed(0)}%)</span>
          </div>
        </div>
      </div>
      
      <div className="h-2 w-full rounded-full overflow-hidden mb-4" style={{ background: 'var(--color-bg-elevated)' }}>
        <div 
          className="h-full rounded-full"
          style={{ 
            width: `${supportedPct}%`, 
            background: 'var(--color-brand)' 
          }}
        />
      </div>
      
      <div className="flex flex-wrap gap-2 mb-3">
        <StateChip
          state={completeness >= 90 ? 'verified' : completeness >= 60 ? 'warning' : 'needs-human'}
          text={`DOCS ${completeness.toFixed(0)}%`}
        />
        <StateChip 
          state={causationState} 
          text={`CAUSATION ${causationLabel || 'N/A'}`} 
        />
        {strongArgs > 0 && (
          <StateChip 
            state="needs-human" 
            text={`${strongArgs} STRONG ARGUMENTS`} 
          />
        )}
        <StateChip 
          state={deniedTotal > 0 ? 'blocker' : 'verified'} 
          text={deniedTotal > 0 ? `${formatCurrency(deniedTotal)} DENIALS` : '— DENIALS'} 
        />
      </div>
      
      <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
        Carrier position reflects {hasGaps ? 'missing documentation' : 'complete documentation'}
        {causationState === 'warning' || causationState === 'needs-human' ? ' and uncertain causation' : ''}; 
        variance driven by {strongArgs > 0 ? 'strong reduction arguments' : 'conservative valuation'}
        {deniedTotal > 0 ? ` with ${formatCurrency(deniedTotal)} in denials` : ' with no denials'}.
      </p>
    </div>
  );
}

/**
 * CarrierBridgeLedger — reconciliation waterfall for the carrier (adjuster)
 * position. Replaces the old per-category ClaimItemCard list (a fabricated
 * 70/15/15 split). Anchor on the engine (CP 00 30) actual-loss-sustained
 * figure, subtract the carrier's judgment-layer haircuts down to the supported
 * position. The two step layers are CARRIER JUDGMENT (AdjusterAnalyzer
 * second-opinion), NOT an ISO CP 00 30 determination — see `note`.
 */
function CarrierBridgeLedger({ bridge }: { bridge: NonNullable<AdjusterAnalysisResult['carrier_bridge']> }) {
  return (
    <div>
      <div
        className="rounded-lg overflow-hidden"
        style={{ border: '1px solid var(--color-border-sub)', background: 'var(--color-bg-elevated)' }}
      >
        {/* Anchor — the engine / CP 00 30 figure */}
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex flex-col pr-4">
            <span className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>
              {bridge.anchor_label}
            </span>
            <span className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>
              Deterministic engine · CP 00 30
            </span>
          </div>
          <FinancialValue value={bridge.anchor} cents size="lg" className="text-[color:var(--color-text-primary)]" />
        </div>

        {/* Carrier-judgment contra steps */}
        {bridge.steps.length > 0 && (
          <div style={{ borderTop: '1px solid var(--color-border-faint)' }}>
            <div className="px-4 pt-2 pb-1">
              <span className="text-[11px] font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>
                {bridge.layer_label || 'Carrier judgment'}
              </span>
            </div>
            {bridge.steps.map((step, idx) => (
              <div key={idx} className="flex items-start justify-between px-4 py-2">
                <div className="flex flex-col pr-4">
                  <span className="cs-body" style={{ color: 'var(--color-text-secondary)' }}>Less — {step.label}</span>
                  {step.basis && (
                    <span className="text-[11px]" style={{ color: 'var(--color-text-muted)' }}>{step.basis}</span>
                  )}
                </div>
                <FinancialValue value={step.amount} contra cents />
              </div>
            ))}
          </div>
        )}

        {/* Ruled total — carrier-supported position */}
        <div className="flex items-center justify-between px-4 py-3" style={{ borderTop: '2px solid var(--color-border)' }}>
          <span className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>
            {bridge.supported_label}
          </span>
          <FinancialValue
            value={bridge.supported}
            cents
            size="lg"
            className="text-[color:var(--color-text-primary)] font-semibold"
          />
        </div>
      </div>

      {/* Honest residual — carrier judgment, not carrier truth / not a clean engine figure */}
      {bridge.note && (
        <div className="mt-3 flex items-start gap-2 rounded-lg p-3" style={{ background: 'rgba(255,102,0,0.06)' }}>
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" style={{ color: 'var(--color-brand)' }} />
          <p className="text-xs leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>{bridge.note}</p>
        </div>
      )}
    </div>
  );
}

function CollapsibleSection({
  title,
  icon: Icon,
  expanded,
  onToggle,
  children,
  badge,
}: {
  title: string;
  icon: React.ElementType;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  badge?: number;
}) {
  return (
    <div className="overflow-hidden" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14 }}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-6 py-4 transition-colors"
        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      >
        <div className="flex items-center gap-3">
          <span className="inline-flex" style={{ color: 'var(--color-text-muted)' }}>
            <Icon className="w-5 h-5" />
          </span>
          <span className="cs-h3">{title}</span>
          {badge !== undefined && badge > 0 && (
            <StateChip state="needs-human" text={`${badge} critical`} />
          )}
        </div>
        {expanded ? (
          <ChevronDown className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
        ) : (
          <ChevronRight className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
        )}
      </button>
      {expanded && (
        <div className="px-6 pb-6 pt-4" style={{ borderTop: '1px solid var(--color-border)' }}>
          {children}
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, chipState, icon: Icon }: { 
  label: string; 
  value: string; 
  chipState?: ChipState;
  icon: React.ElementType;
}) {
  return (
    <div className="rounded-lg p-4" style={{ background: 'var(--color-bg-elevated)' }}>
      <div className="flex items-center gap-2 mb-2">
        <span className="inline-flex" style={{ color: 'var(--color-text-muted)' }}>
          <Icon className="w-4 h-4" />
        </span>
        <span className="cs-label" style={{ color: 'var(--color-text-secondary)' }}>{label}</span>
      </div>
      <div className="flex items-center justify-between">
        <div className="font-mono tabular-nums cs-figure-lg" style={{ color: 'var(--color-text-primary)' }}>{value}</div>
        {chipState && <StateChip state={chipState} text={label} />}
      </div>
    </div>
  );
}

function RiskCard({ label, level, description }: { label: string; level: string; description: string }) {
  return (
    <div className="rounded-lg p-4" style={{ background: 'var(--color-bg-elevated)' }}>
      <h4 className="cs-label mb-1" style={{ color: 'var(--color-text-secondary)' }}>{label}</h4>
      <div className="mb-2">
        <StateChip state={severityToState('risk', level)} text={level} />
      </div>
      <p className="cs-meta" style={{ color: 'var(--color-text-muted)' }}>{description}</p>
    </div>
  );
}

function GapCard({ gap }: { gap: DocumentationGap }) {
  return (
    <div
      className="rounded-lg p-4"
      style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
    >
      <div className="flex items-start justify-between mb-2">
        <h4 className="cs-label" style={{ color: 'var(--color-text-primary)' }}>{gap.description}</h4>
        <StateChip state={severityToState('gap', gap.category)} text={humanizeEnum(gap.category)} />
      </div>
      <p className="cs-body mb-2" style={{ color: 'var(--color-text-secondary)' }}>
        <span style={{ fontWeight: 500 }}>How to resolve:</span> {gap.remediation}
      </p>
      {gap.impact_estimate > 0 && (
        <p className="cs-body font-mono tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
          Estimated impact: <FinancialValue value={gap.impact_estimate} />
        </p>
      )}
    </div>
  );
}

function CausationCard({ assessment }: { assessment: CausationAssessment }) {
  return (
    <div className="rounded-lg p-4" style={{ border: '1px solid var(--color-border)' }}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="cs-label" style={{ color: 'var(--color-text-primary)' }}>{assessment.category}</h4>
        <StateChip state={severityToState('causation', assessment.rating)} text={humanizeEnum(assessment.rating)} />
      </div>
      <p className="cs-body mb-2" style={{ color: 'var(--color-text-secondary)' }}>{assessment.rationale}</p>
      {assessment.alternative_explanations?.length > 0 && (
        <div className="cs-meta mb-2" style={{ color: 'var(--color-text-muted)' }}>
          <span style={{ fontWeight: 500 }}>Alternative explanations: </span>
          {assessment.alternative_explanations.join(', ')}
        </div>
      )}
      {assessment.adjustment_factor !== 1.0 && (
        <div className="font-mono tabular-nums cs-meta" style={{ color: 'var(--color-text-primary)' }}>
          Adjustment factor: {(assessment.adjustment_factor * 100).toFixed(0)}%
        </div>
      )}
    </div>
  );
}

function ReductionArgumentCard({ argument }: { argument: ReductionArgument }) {
  return (
    <div className="rounded-lg p-4" style={{ border: '1px solid var(--color-border)' }}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <span className="cs-meta">{humanizeEnum(argument.argument_type)}</span>
          <h4 className="cs-label" style={{ color: 'var(--color-text-primary)' }}>{argument.description}</h4>
        </div>
        <StateChip state={severityToState('reduction', argument.strength)} text={humanizeEnum(argument.strength)} />
      </div>
      {argument.dollar_impact > 0 && (
        <p className="cs-body mb-2 font-mono tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
          Impact: <FinancialValue value={-argument.dollar_impact} />
        </p>
      )}
      <p className="cs-meta" style={{ color: 'var(--color-text-muted)' }}>
        <span style={{ fontWeight: 500 }}>Counter-evidence needed:</span> {argument.counter_evidence_needed}
      </p>
    </div>
  );
}